import { html } from 'lit';
import { customElement, query, state } from 'lit/decorators.js';
import { connect } from 'pwa-helpers';
import store from '../../../store/store';
import { BaseComponent } from '../../base-component/base-component';
import { StoreState } from '../../../store/store.interface';
import { getSelectedCountryState } from '../../country-selector/country-state';
import { resetSchoolResponse } from '../../user-profile';
import { getLtrListUrl, getLtrSchoolListUrl } from '../utils/ltr.utils';
import emit from '../../../common/events';

/**
 * Component to search for Learning Technology Representatives (LTR) by state and school or by zip code.
 *
 * The state is used to filter schools. The school selection takes the user to a list of LTRs for that school.
 * If a zip code is provided, it takes the user to a list of schools in that area.
 */
@customElement('mhecomm-ltr-search')
export class LearningTechnologyRepresentativeSearch extends connect(store)(BaseComponent) {

  @query('mhecomm-school-selector')
  protected schoolSelector: HTMLFormElement | undefined;

  @state()
  private selectedState: any;

  @state()
  private selectedSchool: any;

  @state()
  private zipcode: string = '';

  private readonly type: string = `ltr-search`;

  stateChanged(state: StoreState) {
    this.selectedState = getSelectedCountryState(this.type, state);
  }

  protected updated(_changedProperties: Map<string | number | symbol, unknown>): void {
    super.updated(_changedProperties);

    if (_changedProperties.has('selectedState')) {
      this.schoolSelector?.resetSelectOption();
      store.dispatch(resetSchoolResponse());
    }
  }

  renderComp() {
    return html`
      <div class="row">
        <div class="row">
          <div class="row">
            <div class="col-12">
              <h3 class="fw-normal">Search by State & School</h3>
            </div>
          </div>
          <div class="row">
            <div class="col-6">
              <mhecomm-country-state-selector
                id="state-selector"
                type="${this.type}"
                label="State:"
                countryCode="US"
                selectedOption=${this.selectedState}
                errorMessage="Please select a valid state"
                ?capitalize=${true}
                ?isdirty=${false}
              >
              </mhecomm-country-state-selector>
            </div>
            <div class="col-6">
              <mhecomm-school-selector
                id="school-selector"
                countryCode="US"
                segment="HE"
                label="School:"
                .stateCode=${this.selectedState}
                errorMessage="Please select a valid school"
                ?isdirty=${false}
                @onSchoolSelection=${(event) => this.onSchoolSelection(event.detail)}
              >
              </mhecomm-school-selector>
            </div>
          </div>
        </div>
        <div class="row my-3">
          <div class="fw-semibold">or</div>
        </div>
        <div class="row">
          <div class="row">
            <div class="col-12">
              <h3 class="fw-normal">Search by Zip Code</h3>
            </div>
          </div>
          <div class="row">
            <div class="col-6">
              <label for="zipcode" class="form-label">Zip Code:</label>
              <input
                type="text" class="form-control mb-3" id="zipcode"
                @input=${(_e: InputEvent) => this.zipcode = (_e.target as HTMLInputElement).value}
              />
            </div>
          </div>
        </div>
      </div>
      <button
        class="btn btn-primary"
        @click=${() => this.onClick()}
        ?disabled=${!this.selectedSchool && this.zipcode.trim() == ''}>
        Search for Local Rep&nbsp
        <uxt-icon icon="arrow-right" aria-hidden="true"></uxt-icon>
      </button>
    `;
  }

  private onSchoolSelection(school) {
    this.selectedSchool = school;
  }

  private onClick() {
    this.emitSearchClickEvent();
    if (this.selectedSchool) {
      this.goToLtrListUrl();
    } else if (this.zipcode.trim() !== '') {
      this.goToLtrSchoolListUrl();
    }
  }

  private emitSearchClickEvent() {
    emit({
      element: this,
      name: 'ltr-search-click',
      detail: {
        eventName: 'ltr-search-click',
        searchType: this.selectedSchool ? 'school' : 'zipcode',
        searchFilters: {
          schoolName: this.selectedSchool?.name,
          schoolId: this.selectedSchool?.xid,
          state: this.selectedSchool ? this.selectedState : undefined,
          zipcode: this.zipcode ?? undefined,
        }
      }
    });
  }

  private goToLtrListUrl() {
    window.location.href = getLtrListUrl(this.selectedSchool.xid);
  }

  private goToLtrSchoolListUrl() {
    window.location.href = getLtrSchoolListUrl(this.zipcode);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-ltr-search': LearningTechnologyRepresentativeSearch;
  }
}
